import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import inventoryApi from '../../../services/inventoryApi';
import InventoryLayout, { 
  Card, 
  CardHeader, 
  SkeletonRow, 
  ActionButton, 
  StatusBadge, 
  EmptyState, 
  ErrorState 
} from '../../../components/admin/InventoryLayout';

// Supplier Modal Component
const SupplierModal = ({ supplier, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: supplier?.name || '',
    contactPerson: supplier?.contactPerson || '',
    email: supplier?.email || '',
    phone: supplier?.phone || '',
    address: supplier?.address || '',
    website: supplier?.website || '',
    notes: supplier?.notes || ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name || '',
        contactPerson: supplier.contactPerson || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: supplier.address || '',
        website: supplier.website || '',
        notes: supplier.notes || ''
      });
    }
  }, [supplier]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving supplier:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader
          title={supplier ? 'Edit Supplier' : 'Add New Supplier'}
          icon={
            <svg className="h-5 w-5 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
        />
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                Company Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
                placeholder="Enter company name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                Contact Person
              </label>
              <input
                type="text"
                value={formData.contactPerson}
                onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                className="w-full px-3 py-2 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
                placeholder="Enter contact person"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
                placeholder="Enter email address"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
                placeholder="Enter phone number"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Address
            </label>
            <textarea
              rows={3}
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full px-3 py-2 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
              placeholder="Enter full address"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Website
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="w-full px-3 py-2 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
              placeholder="Enter website URL"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Notes
            </label>
            <textarea
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
              placeholder="Additional notes about the supplier"
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <ActionButton
              onClick={onClose}
              variant="secondary"
              size="md"
              className="flex-1"
            >
              Cancel
            </ActionButton>
            <ActionButton
              onClick={handleSubmit}
              variant="accent"
              size="md"
              disabled={saving || !formData.name.trim()}
              className="flex-1 !bg-[#7d6633] !text-white hover:!bg-[#6a5429]"
            >
              {saving ? 'Saving...' : (supplier ? 'Update Supplier' : 'Add Supplier')}
            </ActionButton>
          </div>
        </form>
      </Card>
    </div>
  );
};

const Suppliers = () => {
  const { user, hasPermission } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await inventoryApi.suppliers.getSuppliers();
      setSuppliers(response.suppliers || []);
    } catch (err) {
      console.error('Error loading suppliers:', err);
      setError('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
  };

  const handleSave = async (supplierData) => {
    try {
      if (editingSupplier) {
        await inventoryApi.suppliers.updateSupplier(editingSupplier.id, supplierData);
      } else {
        await inventoryApi.suppliers.createSupplier(supplierData);
      }
      await loadSuppliers();
      setEditingSupplier(null);
      setShowAddModal(false);
    } catch (err) {
      console.error('Error saving supplier:', err);
      setError('Failed to save supplier');
    }
  };

  const handleCancel = () => {
    setEditingSupplier(null);
    setShowAddModal(false);
  };

  // Filter suppliers based on search term
  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.contactPerson?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <InventoryLayout 
      title="Suppliers"
      subtitle="Manage your suppliers and their contact information"
      currentTab="suppliers"
      action={
        (hasPermission('inventory.create') || user?.role === 'shop_admin' || user?.role === 'superadmin') && (
          <ActionButton
            onClick={() => setShowAddModal(true)}
            variant="accent"
            size="md"
            className="!bg-[#7d6633] !text-white hover:!bg-[#6a5429] hidden md:inline-flex"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Supplier
          </ActionButton>
        )
      }
    >
      {/* Mobile Header - Like Bookings Page */}
      <div className="md:hidden mb-6 pt-2">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-12 w-12 rounded-xl bg-[#7d6633]/10 flex items-center justify-center flex-shrink-0">
            <svg className="h-6 w-6 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Suppliers</h1>
            <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">
              Manage your suppliers and their contact information
            </p>
          </div>
        </div>
        
        {/* Mobile Action Button */}
        {(hasPermission('inventory.create') || user?.role === 'shop_admin' || user?.role === 'superadmin') && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowAddModal(true)}
            className="inline-flex px-3 py-2 rounded-xl text-white text-sm font-medium items-center gap-1.5 transition-all duration-200 hover:shadow-lg whitespace-nowrap"
            style={{ background: '#7d6633' }}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Create
          </motion.button>
        )}
      </div>

      {error && <ErrorState error={error} onRetry={loadSuppliers} />}

      {/* Suppliers Table */}
      <Card>
        <CardHeader
          title="Suppliers Directory"
          subtitle="Manage supplier contacts and information"
          icon={
            <svg className="h-5 w-5 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
        />
        
        <div className="p-4 md:p-5">
          {/* Search (separate line) + count */}
          <div className="mb-4 space-y-2 md:flex md:items-center md:justify-between md:space-y-0 md:gap-4">
            <div className="relative w-full md:max-w-md">
              <svg className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full min-h-[44px] pl-10 pr-4 py-2.5 border border-white/20 dark:border-white/10 rounded-xl bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
              />
            </div>

            <div className="md:flex md:items-center md:justify-end md:flex-shrink-0">
              <StatusBadge status={`${filteredSuppliers.length} supplier${filteredSuppliers.length !== 1 ? 's' : ''}`} variant="info" />
            </div>
          </div>

          {loading ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-500 dark:text-zinc-400 border-b border-white/10 dark:border-white/10">
                    <th className="py-4 px-4 font-medium">Company</th>
                    <th className="py-4 px-4 font-medium">Contact Person</th>
                    <th className="py-4 px-4 font-medium">Email</th>
                    <th className="py-4 px-4 font-medium">Phone</th>
                    <th className="py-4 px-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10 dark:divide-white/10">
                  {[...Array(3)].map((_, i) => (
                    <SkeletonRow key={i} columns={5} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : filteredSuppliers.length === 0 ? (
            searchTerm ? (
              <EmptyState
                icon={
                  <svg className="h-6 w-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                }
                title="No suppliers found"
                description={`No suppliers match your search for "${searchTerm}".`}
                action={
                  <ActionButton
                    onClick={() => setSearchTerm('')}
                    variant="secondary"
                    size="sm"
                  >
                    Clear search
                  </ActionButton>
                }
              />
            ) : (
              <EmptyState
                icon={
                  <svg className="h-6 w-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                }
                title="No suppliers yet"
                description="Get started by adding your first supplier to manage your inventory sources."
                action={
                  (hasPermission('inventory.create') || user?.role === 'shop_admin' || user?.role === 'superadmin') && (
                    <ActionButton
                      onClick={() => setShowAddModal(true)}
                      variant="accent"
                      size="md"
                      className="!bg-[#7d6633] !text-white hover:!bg-[#6a5429]"
                    >
                      Add First Supplier
                    </ActionButton>
                  )
                }
              />
            )
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-500 dark:text-zinc-400 border-b border-white/10 dark:border-white/10">
                    <th className="py-4 px-4 font-medium">Company</th>
                    <th className="py-4 px-4 font-medium">Contact Person</th>
                    <th className="py-4 px-4 font-medium">Email</th>
                    <th className="py-4 px-4 font-medium">Phone</th>
                    <th className="py-4 px-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10 dark:divide-white/10">
                  {filteredSuppliers.map((supplier) => (
                    <motion.tr 
                      key={supplier.id}
                      className="text-zinc-800 dark:text-zinc-100 hover:bg-white/30 dark:hover:bg-zinc-800/30 transition-colors"
                      whileHover={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                    >
                      <td className="py-4 px-4">
                        <div>
                          <div className="font-medium text-zinc-900 dark:text-zinc-100">{supplier.name}</div>
                          {supplier.website && (
                            <a 
                              href={supplier.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-[#7d6633] hover:underline"
                            >
                              {supplier.website}
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-zinc-800 dark:text-zinc-100">
                        {supplier.contactPerson || '—'}
                      </td>
                      <td className="py-4 px-4">
                        {supplier.email ? (
                          <a 
                            href={`mailto:${supplier.email}`}
                            className="text-[#7d6633] hover:underline"
                          >
                            {supplier.email}
                          </a>
                        ) : '—'}
                      </td>
                      <td className="py-4 px-4">
                        {supplier.phone ? (
                          <a 
                            href={`tel:${supplier.phone}`}
                            className="text-[#7d6633] hover:underline"
                          >
                            {supplier.phone}
                          </a>
                        ) : '—'}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <ActionButton
                            onClick={() => handleEdit(supplier)}
                            variant="accent"
                            size="xs"
                          >
                            <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                          </ActionButton>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Supplier Modal */}
      {(showAddModal || editingSupplier) && (
        <SupplierModal
          supplier={editingSupplier}
          isOpen={showAddModal || !!editingSupplier}
          onClose={handleCancel}
          onSave={handleSave}
        />
      )}
    </InventoryLayout>
  );
};

export default Suppliers;